// ═══════════════════════════════════════════════════════════════════════════════
// GPS Toggle Component
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

type GpsToggleProps = {
  enabled: boolean;
  onToggle: (enabled: boolean, lat: number | '', lng: number | '') => void;
  disabled?: boolean;
};

export const GpsToggle = ({ enabled, onToggle, disabled = false }: GpsToggleProps) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleToggle = useCallback(async () => {
    if (disabled) return;

    if (enabled) {
      onToggle(false, '', '');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        });
      });

      onToggle(true, position.coords.latitude, position.coords.longitude);
    } catch (err) {
      const geoError = err as GeolocationPositionError;
      if (geoError.code === geoError.PERMISSION_DENIED) {
        setError('GPS permission denied');
      } else if (geoError.code === geoError.POSITION_UNAVAILABLE) {
        setError('GPS unavailable');
      } else {
        setError('GPS timeout');
      }
      onToggle(false, '', '');
    } finally {
      setLoading(false);
    }
  }, [enabled, onToggle, disabled]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <svg
            className={`h-5 w-5 ${enabled ? 'text-green-600' : 'text-gray-400'}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          <span className="text-sm font-medium text-gray-700">
            {t('task.complete.gpsSection')}
          </span>
        </div>

        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          tabIndex={0}
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          disabled={disabled || loading}
          className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
            enabled ? 'bg-green-500' : 'bg-gray-200'
          } ${disabled || loading ? 'cursor-not-allowed opacity-50' : ''}`}
          aria-label={t('task.complete.gpsSection')}
        >
          <span
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
              enabled ? 'translate-x-5' : 'translate-x-0'
            }`}
          >
            {loading && (
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-primary-600" />
              </span>
            )}
          </span>
        </button>
      </div>

      <p className="text-xs text-gray-500">{t('task.complete.gpsHint')}</p>

      {enabled && (
        <p className="text-xs font-medium text-green-600">
          {t('task.complete.gpsOn')}
        </p>
      )}

      {error && (
        <p className="text-xs text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};
